import type { PlanDocument, PlanRow } from "@/features/app/types";

type PlanSectionKey = keyof PlanDocument;

type PlanSection = {
  key: PlanSectionKey;
  heading: string;
  aliases: string[];
};

const planSections: PlanSection[] = [
  {
    key: "keyRequirements",
    heading: "Key Tender Requirements",
    aliases: ["key tender requirements", "tender requirements", "requirements"]
  },
  {
    key: "risksAndAssumptions",
    heading: "Risks And Assumptions",
    aliases: ["risks and assumptions", "risks", "assumptions"]
  },
  {
    key: "differentiators",
    heading: "Differentiators",
    aliases: ["differentiators", "why framna should win"]
  },
  {
    key: "proposalOutline",
    heading: "Proposal Outline",
    aliases: ["proposal outline", "outline", "recommended structure"]
  },
  {
    key: "keyPricing",
    heading: "Key Pricing",
    aliases: ["key pricing", "pricing", "commercial considerations"]
  }
];

const stripListMarker = (line: string): string => line.replace(/^\s*(?:[-*+]|\d+\.)\s+/, "").trim();

const normalizeHeading = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const findSectionByHeading = (heading: string): PlanSection | null => {
  const normalized = normalizeHeading(heading);
  return planSections.find((section) => section.aliases.includes(normalized)) ?? null;
};

export const createEmptyPlanDocument = (): PlanDocument => ({
  keyRequirements: [],
  risksAndAssumptions: [],
  differentiators: [],
  proposalOutline: [],
  keyPricing: []
});

export const planDocumentToMarkdown = (document: PlanDocument): string =>
  planSections
    .map((section) => {
      const items = document[section.key];
      const body = items.length > 0 ? items.map((item) => `- ${item}`).join("\n") : "- ";
      return `## ${section.heading}\n${body}`;
    })
    .join("\n\n");

export const planToMarkdown = (plan: PlanRow | null): string =>
  plan ? planDocumentToMarkdown(plan.document) : planDocumentToMarkdown(createEmptyPlanDocument());

export const markdownToPlanDocument = (markdown: string): PlanDocument => {
  const document = createEmptyPlanDocument();
  let currentSection: PlanSection | null = null;

  for (const rawLine of markdown.split("\n")) {
    const headingMatch = rawLine.match(/^\s{0,3}#{1,6}\s+(.+?)\s*$/);
    if (headingMatch) {
      currentSection = findSectionByHeading(headingMatch[1]);
      continue;
    }

    if (!currentSection) {
      continue;
    }

    const trimmed = stripListMarker(rawLine);
    if (!trimmed) {
      continue;
    }

    document[currentSection.key].push(trimmed);
  }

  return document;
};
